import { memo } from 'react';
import { capitalize } from 'lodash';
import {
  CircularProgress, Grid, Paper, Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { useInstanceSelector } from '@client/hooks/redux.hooks';
import { inProgress } from '@client/selectors/helpers/loaders';
import type { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import type { PublicImageUrl } from '@sda/interfaces/image';

const useStyles = makeStyles({ name: 'DropContainer' })((theme) => ({
  root: {
    cursor: 'pointer',
    height: '100%',
    position: 'relative',
    width: '100%',
    borderRadius: theme.radii.medium,

    '&::before': {
      content: '""',
      display: 'block',
      paddingTop: '56.25%',
    },
  },
  container: {
    marginTop: theme.spacing(1),
    borderRadius: theme.radii.medium,

    '&:focus-visible': {
      outlineStyle: 'auto',
      outlineColor: theme.palette.primary.main,
    },
  },
  dragInactiveInherit: {
    backgroundColor: theme.palette.grey[200],
  },
  dragActiveInherit: {
    backgroundColor: theme.palette.grey[300],

    '& svg': {
      color: theme.palette.grey[600],
    },
  },
  dragInactivePaper: {
    backgroundColor: theme.palette.grey[50],

    '& svg': {
      color: `${theme.palette.grey[500]} !important`,
    },
  },
  dragActivePaper: {
    backgroundColor: theme.palette.grey[100],

    '& svg': {
      color: `${theme.palette.grey[500]} !important`,
    },
  },
  uploadIcon: {
    fontSize: 24,
    color: theme.palette.grey[500],
  },
  uploadText: {
    textAlign: 'center',
    color: theme.typography.caption.color,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
  },
  disabled: {
    display: 'none',
  },
  fullWidth: {
    width: '100%',
    borderRadius: theme.radii.large,

    '& svg': {
      fontSize: 28,
    },
  },
  hideInput: {
    display: 'none',
  },
}));

export interface DropContainerProps {
  readonly isActive: boolean;
  readonly value: PublicImageUrl[];
  readonly scope: string;
  readonly name: string;
  readonly multiple?: boolean;
  readonly maxFiles?: number;
  readonly columnWidth?: number;
  readonly disabled?: boolean;
  readonly id: string;
  readonly rootProps: () => DropzoneRootProps;
  readonly inputProps: () => DropzoneInputProps;
  readonly onClick?: () => void;
  readonly color: 'inherit' | 'paper';
}

export const DropContainer = memo<DropContainerProps>(({
  isActive, rootProps, value, scope,
  inputProps, name, multiple, maxFiles,
  columnWidth, disabled, id, onClick,
  color,
}) => {
  const { classes, cx } = useStyles();
  const isInProgress = useInstanceSelector(inProgress, scope);

  let rootClass = classes.root;

  if (isActive === false) {
    rootClass = cx(rootClass, classes[`dragInactive${capitalize(color)}`]);
  } else if (isActive === true) {
    rootClass = cx(rootClass, classes[`dragActive${capitalize(color)}`]);
  }

  let isVisible = true;

  if (!isInProgress && value && value.length > 0 && multiple === false) {
    isVisible = false;
  } else if (value && value.length >= maxFiles) {
    isVisible = false;
  }

  if (disabled || !isVisible) {
    return null;
  }

  const extraProps = onClick ? { onClick } : rootProps();

  return (

    <Grid
      item
      xs={multiple ? columnWidth : 12}
    >
      <Tooltip
        title="Click or drag and drop a file to upload"
        placement="top"
      >
        <div
          className={classes.container}
          {...extraProps}
        >
          <Paper
            className={cx(
              rootClass,
              multiple === false && classes.fullWidth,
            )}
          >
            <div className={classes.uploadText}>
              {isInProgress ? (
                <>
                  <CircularProgress size={24} />

                  <Typography variant="subtitle1">
                    Uploading...
                  </Typography>
                </>
              ) : (
                <AddAPhotoIcon className={classes.uploadIcon} />
              )}
            </div>
          </Paper>

          <input
            {...inputProps()}
            className={classes.hideInput}
            id={id}
            name={name}
          />
        </div>
      </Tooltip>
    </Grid>
  );
});
